import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Zap
} from 'lucide-react';
import { theme } from '../styles/theme';
import { FinalReport } from '../types';
import FinalReportSummary from './FinalReportSummary';
import FinalReportViewer from './FinalReportViewer';

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
  min-height: 100%;
  flex: 1;
`;

const SplitContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: auto;
  min-height: 80vh;
  max-height: none;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.05);
  background: linear-gradient(135deg, 
    ${theme.colors.surface} 0%, 
    ${theme.colors.backgroundSecondary} 100%
  );
  position: relative;
  
  /* Allow children to handle their own scrolling */
  > * {
    overflow: auto;
    max-height: 100%;
  }
  
  /* AI-inspired glow effect */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      ${theme.colors.primary[500]}40, 
      ${theme.colors.accent[500]}40,
      ${theme.colors.primary[500]}40
    );
    border-radius: ${theme.borderRadius.xl};
    z-index: -1;
    animation: borderGlow 4s linear infinite;
  }
  
  @keyframes borderGlow {
    0%, 100% { 
      opacity: 0.5;
      filter: blur(1px);
    }
    50% { 
      opacity: 0.8;
      filter: blur(2px);
    }
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    min-height: 75vh;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    min-height: 80vh;
  }
`;

const ProcessingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: linear-gradient(135deg, 
    ${theme.colors.primary[100]}60, 
    ${theme.colors.accent[100]}40
  );
  border: 1px solid ${theme.colors.primary[300]}60;
  border-radius: ${theme.borderRadius.xl};
  color: ${theme.colors.primary[800]};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-bottom: ${theme.spacing[6]};
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.3), 
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const ProcessingDot = styled.div`
  width: 12px;
  height: 12px;
  background: linear-gradient(45deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.full};
  animation: aiPulse 1.5s infinite ease-in-out;
  box-shadow: 0 0 8px ${theme.colors.primary[500]}40;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, ${theme.colors.primary[500]}20, ${theme.colors.accent[500]}20);
    border-radius: ${theme.borderRadius.full};
    animation: aiPulse 1.5s infinite ease-in-out reverse;
  }
  
  @keyframes aiPulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.7;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: linear-gradient(135deg, 
    ${theme.colors.backgroundSecondary}, 
    ${theme.colors.surface}
  );
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  position: relative;
  backdrop-filter: blur(5px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, 
      ${theme.colors.primary[500]}05 0%,
      transparent 50%
    );
    border-radius: inherit;
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, 
    ${theme.colors.primary[100]}, 
    ${theme.colors.accent[100]}
  );
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[6]};
  color: ${theme.colors.primary[600]};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.8);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      ${theme.colors.primary[500]}30, 
      ${theme.colors.accent[500]}30
    );
    border-radius: ${theme.borderRadius.full};
    z-index: -1;
    animation: emptyIconGlow 3s ease-in-out infinite alternate;
  }
  
  @keyframes emptyIconGlow {
    0% { opacity: 0.3; }
    100% { opacity: 0.6; }
  }
`;

const EmptyTitle = styled.h3`
  margin: 0 0 ${theme.spacing[3]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.xl};
  text-align: center;
  letter-spacing: -0.3px;
`;

const EmptyDescription = styled.p`
  margin: 0;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
  max-width: 500px;
  line-height: ${theme.typography.lineHeight.relaxed};
  text-align: center;
  font-weight: ${theme.typography.fontWeight.medium};
  opacity: 0.9;
`;

const OverviewSection = styled.div`
  background: linear-gradient(135deg, 
    ${theme.colors.primary[100]}30, 
    ${theme.colors.accent[100]}20,
    ${theme.colors.primary[50]}40
  );
  border: 1px solid ${theme.colors.primary[500]}50;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -30%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, 
      ${theme.colors.primary[500]}15 0%, 
      ${theme.colors.accent[500]}10 30%,
      transparent 70%
    );
    border-radius: 50%;
    animation: floatGlow 8s ease-in-out infinite alternate;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -20%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, 
      ${theme.colors.accent[500]}12 0%, 
      transparent 60%
    );
    border-radius: 50%;
    animation: floatGlow 6s ease-in-out infinite alternate-reverse;
  }
  
  @keyframes floatGlow {
    0% {
      transform: translate(0, 0) scale(1);
      opacity: 0.6;
    }
    100% {
      transform: translate(10px, -10px) scale(1.1);
      opacity: 0.8;
    }
  }
`;

const OverviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const OverviewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, 
    ${theme.colors.primary[500]}, 
    ${theme.colors.accent[500]},
    ${theme.colors.primary[600]}
  );
  border-radius: ${theme.borderRadius.lg};
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 
              0 4px 8px ${theme.colors.primary[500]}30;
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.3), 
      transparent
    );
    animation: iconShimmer 3s infinite;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2), 
                0 6px 12px ${theme.colors.primary[500]}40;
  }
  
  @keyframes iconShimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const OverviewContent = styled.div`
  position: relative;
  z-index: 1;

  h2 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize['2xl']};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    letter-spacing: -0.5px;
    background: linear-gradient(135deg, 
      ${theme.colors.textPrimary}, 
      ${theme.colors.primary[600]}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    margin: ${theme.spacing[2]} 0 0 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.medium};
    opacity: 0.9;
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

interface FinalReportResultsProps {
  finalReport?: FinalReport;
  isProcessing?: boolean;
  /** When true, the run was started in GENERATE mode — passed to children
   *  so headings ("Refined" → "Generated") swap appropriately. */
  isGenerateMode?: boolean;
}

const FinalReportResults: React.FC<FinalReportResultsProps> = ({
  finalReport,
  isProcessing = false,
  isGenerateMode = false,
}) => {
  const [activeSection, setActiveSection] = useState<string>('');

  const handleNavigateToSection = (section: string) => {
    setActiveSection(section);
  };

  // Always show content when processing or when we have data
  const shouldShowContent = isProcessing || finalReport;

  if (!shouldShowContent) {
    return (
      <ReportContainer>
        <EmptyState>
          <EmptyIcon>
            <FileText size={32} />
          </EmptyIcon>
          <EmptyTitle>Final Report Pending</EmptyTitle>
          <EmptyDescription>
            The comprehensive final report will be generated once all analysis stages 
            are complete. This report will include evaluation criteria, submission 
            justification, and all refined tender responses in a professional format 
            ready for submission or review.
          </EmptyDescription>
        </EmptyState>
      </ReportContainer>
    );
  }

  return (
    <ReportContainer>
      {isProcessing && (
        <ProcessingIndicator>
          <ProcessingDot />
          Generating comprehensive final report with recommendations...
        </ProcessingIndicator>
      )}

      {/* Always show content when processing, even if report is not yet complete */}
      <>
        {/* Overview Section */}
        <OverviewSection>
          <OverviewHeader>
            <OverviewIcon>
              <Zap size={32} />
            </OverviewIcon>
            <OverviewContent>
              <h2>AI-Powered Final Report</h2>
              <p>Comprehensive analysis with intelligent insights and optimised responses</p>
            </OverviewContent>
          </OverviewHeader>
        </OverviewSection>

        {/* Dynamic Split Screen Layout */}
        <SplitContainer>
          <FinalReportSummary
            finalReport={finalReport || {
              date: new Date().toISOString(),
              overall_score: 0,
              max_score: 100,
              evaluation_criteria: [],
              submission_justification: isProcessing ? 'Generating report...' : '',
              refined_responses: [],
              full_report_markdown: isProcessing ? 'Generating report...' : ''
            }}
            onNavigateToSection={handleNavigateToSection}
            isLoading={isProcessing && !finalReport}
            isGenerateMode={isGenerateMode}
          />
          <FinalReportViewer
            finalReport={finalReport || {
              date: new Date().toISOString(),
              overall_score: 0,
              max_score: 100,
              evaluation_criteria: [],
              submission_justification: isProcessing ? 'Generating report...' : '',
              refined_responses: [],
              full_report_markdown: isProcessing ? 'Generating comprehensive final report...' : ''
            }}
            isLoading={isProcessing && !finalReport}
            activeSection={activeSection}
            isGenerateMode={isGenerateMode}
          />
        </SplitContainer>
      </>
    </ReportContainer>
  );
};

export default FinalReportResults;